import { useCallback } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { createGuid } from 'utils';
import {
  addMyCertification,
  updateMyCertification,
} from 'modules/state-mgmt/workerData';
import { getCertificationDetails } from 'modules/state-mgmt/onboarding';
import { transformCertificationForEdit } from '../../Certifications/utils/transformations';
import { CertificationFormData } from '../../Certifications/types';
import { getImageFormat } from 'utils/files';

interface RefreshParams {
  pageNumber: number;
  pageSize: number;
  filterCertificateStatus: number;
  sortCriteria?: string;
  projectIds?: string[];
}

export const useMyCertificationOperations = (
  typeOptions: any[],
  modalState: any,
  setEditData: (data: any) => void,
  setLoading: (loading: boolean) => void,
  closeModal: () => void,
) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { workerId, projectId, languageCode } = useAppSelector(state => ({
    workerId: state.onboarding.id,
    projectId: state.onboarding?.temp?.project?.value,
    languageCode: state.app?.languageCode,
  }));

  const prepareCertificationPayload = useCallback(
    (certificationData: CertificationFormData) => {
      const selectedCertType =
        certificationData.type && typeof certificationData.type === 'object' && 'value' in certificationData.type
          ? certificationData.type.value
          : null;

      return {
        certificationId: selectedCertType?.id || '',
        idNumber: certificationData.certificationId || '',
        description: certificationData.description || '',
        completionDate: certificationData.issueDate ? moment(certificationData.issueDate).format('YYYY-MM-DD') : '',
        expirationDate: certificationData.expirationDate
          ? moment(certificationData.expirationDate).format('YYYY-MM-DD')
          : null,
        // projectId: projectId || null,
        workerId: workerId || '',
      };
    },
    [workerId],
  );

  const prepareImagesForUpload = useCallback((images: any[], isEdit = false) => {
    return (
      images
        ?.filter(image => {
          if (isEdit) {
            return (image.image || image.url) && !image.isExisting;
          }
          return image.image || image.url;
        })
        .map(image => {
          const imageData = image.image || image.url;
          const imageFormat = getImageFormat(imageData);
          return {
            tempId: image.tempId,
            image: imageData,
            fileName: image.fileName || `${createGuid()}.${imageFormat}`,
          };
        }) || []
    );
  }, []);

  const addNewCertification = useCallback(
    async (payload: any, images: any[], refreshParams: RefreshParams) => {
      try {
        const imagesToUpload = prepareImagesForUpload(images, false);

        await dispatch(
          addMyCertification({
            workerId,
            certificationData: payload,
            images: imagesToUpload,
            languageCode,
            ...refreshParams,
          }),
        ).unwrap();
      } catch (error) {
        const errorMessage = t('certifications.errors.failedToAdd', 'Failed to add certification');
        enqueueSnackbar(errorMessage, { variant: 'error' });
        throw error;
      }
    },
    [dispatch, workerId, languageCode, prepareImagesForUpload, t],
  );

  const updateExistingCertification = useCallback(
    async (payload: any, images: any[], deletedFileIds: string[] = [], refreshParams: RefreshParams) => {
      try {
        const imagesToUpload = prepareImagesForUpload(images, true);

        await dispatch(
          updateMyCertification({
            workerId,
            certificationId: modalState.editCertificationId,
            certificationData: payload,
            images: imagesToUpload,
            deletedFileIds,
            languageCode,
            ...refreshParams,
          }),
        ).unwrap();
      } catch (error) {
        const errorMessage = t('certifications.errors.failedToUpdate', 'Failed to update certification');
        enqueueSnackbar(errorMessage, { variant: 'error' });
        throw error;
      }
    },
    [dispatch, workerId, languageCode, modalState.editCertificationId, prepareImagesForUpload, t],
  );

  const saveCertification = useCallback(
    async (certificationData: CertificationFormData, refreshParams: RefreshParams) => {
      try {
        if (!workerId) {
          throw new Error('Worker ID not available');
        }

        const payload = prepareCertificationPayload(certificationData);

        if (modalState.mode === 'edit') {
          await updateExistingCertification(
            payload,
            certificationData.images,
            certificationData.deletedFileIds || [],
            refreshParams,
          );
        } else {
          await addNewCertification(payload, certificationData.images, refreshParams);
        }

        closeModal();
      } catch (error) {
        const errorMessage = t('certifications.errors.failedToSave', 'Failed to save certification');
        enqueueSnackbar(errorMessage, { variant: 'error' });
        throw error;
      }
    },
    [
      workerId,
      modalState.mode,
      prepareCertificationPayload,
      updateExistingCertification,
      addNewCertification,
      closeModal,
      t,
    ],
  );

  const loadCertificationDetails = useCallback(
    async (certificateId: string) => {
      try {
        if (!workerId) {
          throw new Error('Worker ID not available');
        }

        const certificationDetails = await dispatch(
          getCertificationDetails({
            workerId,
            certificationId: certificateId,
            languageCode,
          }),
        ).unwrap();

        const editFormData = transformCertificationForEdit(certificationDetails, projectId, typeOptions);
        setEditData(editFormData);
      } catch (error) {
        const errorMessage = t('certifications.errors.failedToLoadDetails', 'Failed to load certification details');
        enqueueSnackbar(errorMessage, { variant: 'error' });
        throw error;
      }
    },
    [dispatch, workerId, languageCode, projectId, typeOptions, setEditData, t],
  );

  return {
    saveCertification,
    loadCertificationDetails,
  };
};
